import React from 'react';
import { ArrowUp, Download, Edit3, FolderInput, LayoutGrid, Link as LinkIcon, List, Trash2 } from 'lucide-react';
import { NewItemButton, SecondaryButton, ThreeDotMenuButton, UploadFilesButton, StandardButton } from '@/styles/StandardButtons';
import { cn } from '@/lib/utils';
import type { DriveItem, DriveViewMode } from '../types';

interface DriveToolbarProps {
  canGoUp: boolean;
  onGoUp: () => void;
  onNewFolder: () => void;
  onAddUrl: () => void;
  onUploadFiles: (files: File[]) => void;
  selected: DriveItem[];
  onRename: (item: DriveItem) => void;
  onDownload: (items: DriveItem[]) => void;
  onMove: (items: DriveItem[]) => void;
  onDelete: (items: DriveItem[]) => void;
  onClearSelection: () => void;
  viewMode: DriveViewMode;
  onViewChange: (mode: DriveViewMode) => void;
  /** A client only browses and downloads: no new folder / upload / link, no rename / move / delete. */
  readOnly?: boolean;
}

export function DriveToolbar({
  canGoUp, onGoUp, onNewFolder, onAddUrl, onUploadFiles, selected, onRename, onDownload, onMove, onDelete, onClearSelection, viewMode, onViewChange, readOnly = false,
}: DriveToolbarProps) {
  const files = selected.filter((i) => i.type === 'file');
  const locked = readOnly || selected.some((i) => i.system_key); // a default shared folder is fixed
  const viewButton = (mode: DriveViewMode, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={viewMode === mode}
      title={label}
      onClick={() => onViewChange(mode)}
      className={cn('h-10 w-10 flex items-center justify-center text-gray-500 hover:bg-gray-50', viewMode === mode && 'bg-gray-100 text-gray-900')}
    >
      {icon}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <StandardButton customVariant="white" aria-label="Go to parent folder" title="Go to parent folder" disabled={!canGoUp} onClick={onGoUp}>
          <ArrowUp className="w-4 h-4" />
        </StandardButton>
        {!readOnly && (
          <>
            <NewItemButton label="New Folder" onClick={onNewFolder} />
            <UploadFilesButton onUpload={(list) => list && list.length > 0 && onUploadFiles(Array.from(list))} />
            <SecondaryButton onClick={onAddUrl}>
              <LinkIcon className="w-4 h-4 mr-2" />
              Add URL
            </SecondaryButton>
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        {selected.length > 0 && (
          <>
            <span className="font-subheading-text">{selected.length} selected</span>
            <button type="button" className="text-sm text-blue-600 hover:underline" onClick={onClearSelection}>Clear</button>
            <ThreeDotMenuButton
              items={[
                ...(!locked && selected.length === 1 ? [{ label: selected[0].type === 'url' ? 'Edit' : 'Rename', icon: <Edit3 className="w-4 h-4" />, onClick: () => onRename(selected[0]) }] : []),
                ...(files.length > 0 ? [{ label: 'Download', icon: <Download className="w-4 h-4" />, onClick: () => onDownload(files) }] : []),
                ...(!locked ? [
                  { label: 'Move', icon: <FolderInput className="w-4 h-4" />, onClick: () => onMove(selected) },
                  { label: 'Delete', icon: <Trash2 className="w-4 h-4" />, onClick: () => onDelete(selected), destructive: true },
                ] : []),
              ]}
            />
          </>
        )}
        <div className="flex border border-gray-300 rounded-md overflow-hidden divide-x divide-gray-300">
          {viewButton('list', 'List view', <List className="w-4 h-4" />)}
          {viewButton('grid', 'Thumbnails view', <LayoutGrid className="w-4 h-4" />)}
        </div>
      </div>
    </div>
  );
}
